import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, getUser, API } from "@/lib/api";
import Shell from "@/components/Shell";
import {
    Loader2, Upload, Bot, Download, Lock, Unlock, Plus, Trash2,
    AlertTriangle, FileDigit, Split, Check, Save, FileText, History, Activity, TrendingDown, TrendingUp, Pencil
} from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, ReferenceLine, Cell
} from "recharts";

const CATEGORIES = [
    "RENT", "PAYROLL", "UTILITIES", "SOFTWARE", "MARKETING", "TRAVEL",
    "OFFICE", "EQUIPMENT", "TAX", "INSURANCE", "PROFESSIONAL_FEES",
    "INTEREST", "REVENUE_SERVICES", "REVENUE_PRODUCT", "REVENUE_OTHER",
    "BANK_FEES", "MISC", "UNCATEGORIZED",
];

const fmt = (n) => (n == null ? "—" : `£${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export default function Dossier() {
    const { id } = useParams();
    const me = getUser();
    const [d, setD] = useState(null);
    const [txns, setTxns] = useState([]);
    const [reports, setReports] = useState(null);
    const [predict, setPredict] = useState(null);
    const [audit, setAudit] = useState([]);
    const [tab, setTab] = useState("adjust");
    const [busy, setBusy] = useState("");
    const [notice, setNotice] = useState("");
    // Iter 31.172 — inline dossier-name rename (internal-role only, non-locked).
    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState("");
    const fileRef = useRef();

    const isInternal = me?.role === "internal";
    const locked = d?.status === "locked";

    const loadAll = async () => {
        const [a, b, c, p, au] = await Promise.all([
            api.get(`/dossiers/${id}`),
            api.get(`/dossiers/${id}/transactions`),
            api.get(`/dossiers/${id}/reports`),
            api.get(`/dossiers/${id}/predict`),
            api.get(`/dossiers/${id}/audit`),
        ]);
        setD(a.data);
        setTxns(b.data);
        setReports(c.data);
        setPredict(p.data);
        setAudit(au.data);
    };

    useEffect(() => { loadAll(); }, [id]);

    const showNotice = (msg) => { setNotice(msg); setTimeout(() => setNotice(""), 3500); };

    const upload = async (file) => {
        setBusy("upload");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/dossiers/${id}/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            showNotice(`Ingested ${data.ingested} transmissions from ${data.filename}`);
            loadAll();
        } catch (e) {
            showNotice(`// ERROR: ${e.response?.data?.detail || "upload failed"}`);
        } finally { setBusy(""); }
    };

    const aiSuggest = async () => {
        setBusy("ai");
        try {
            const { data } = await api.post(`/dossiers/${id}/ai-suggest`);
            showNotice(data.updated ? `AI classified ${data.updated} rows` : data.note || "Nothing to classify");
            loadAll();
        } catch (e) {
            showNotice(`// LLM ERROR: ${e.response?.data?.detail || "failed"}`);
        } finally { setBusy(""); }
    };

    const exportPdf = async () => {
        setBusy("pdf");
        try {
            const resp = await api.get(`/dossiers/${id}/export`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `${d.code}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } finally { setBusy(""); }
    };

    const toggleLock = async () => {
        setBusy("lock");
        try {
            await api.post(`/dossiers/${id}/${locked ? "unlock" : "lock"}`);
            loadAll();
        } finally { setBusy(""); }
    };

    if (!d) {
        return (
            <Shell>
                <div className="flex items-center gap-3 text-ink-soft font-accent text-xs uppercase tracking-[0.3em]">
                    <Loader2 className="w-4 h-4 animate-spin" /> Accessing mainframe...
                </div>
            </Shell>
        );
    }

    return (
        <Shell>
            <div className="flex items-start justify-between mb-6 gap-6 flex-wrap">
                <div>
                    <Link to="/dossiers" className="font-accent text-[10px] uppercase tracking-[0.3em] text-ink-soft hover:text-stamp-red" data-testid="back-link">
                        ← Back to vault
                    </Link>
                    <div className="flex items-center gap-4 mt-2">
                        <div className="font-accent text-[10px] uppercase tracking-[0.3em] text-stamp-red">
                            // {d.code}
                        </div>
                        {locked ? (
                            <span className="classified-stamp" data-testid="dossier-sealed-stamp">SEALED</span>
                        ) : (
                            <span className="border border-stamp-red text-stamp-red text-[10px] font-accent tracking-[0.25em] px-2 py-0.5 uppercase">DRAFT</span>
                        )}
                    </div>
                    <h1 className="font-heading text-5xl tracking-[0.04em] uppercase mt-1" data-testid="dossier-title">
                        {editingName ? (
                            <span className="inline-flex items-center gap-2">
                                <input
                                    type="text"
                                    value={nameDraft}
                                    onChange={(e) => setNameDraft(e.target.value)}
                                    onKeyDown={async (e) => {
                                        if (e.key === "Escape") { setEditingName(false); }
                                        if (e.key === "Enter") {
                                            const next = nameDraft.trim();
                                            if (!next || next === d.name) { setEditingName(false); return; }
                                            setBusy("rename");
                                            try {
                                                const { data } = await api.patch(`/dossiers/${d.id}`, { name: next });
                                                setD(data);
                                                setEditingName(false);
                                                setNotice("Dossier renamed.");
                                            } catch (err) {
                                                setNotice(err?.response?.data?.detail || "Rename failed.");
                                            } finally {
                                                setBusy("");
                                            }
                                        }
                                    }}
                                    autoFocus
                                    data-testid="dossier-name-input"
                                    className="bg-paper border-b-2 border-stamp-red font-heading text-5xl tracking-[0.04em] uppercase px-1 py-0 outline-none min-w-[260px]"
                                />
                                <button
                                    onClick={async () => {
                                        const next = nameDraft.trim();
                                        if (!next || next === d.name) { setEditingName(false); return; }
                                        setBusy("rename");
                                        try {
                                            const { data } = await api.patch(`/dossiers/${d.id}`, { name: next });
                                            setD(data);
                                            setEditingName(false);
                                            setNotice("Dossier renamed.");
                                        } catch (err) {
                                            setNotice(err?.response?.data?.detail || "Rename failed.");
                                        } finally {
                                            setBusy("");
                                        }
                                    }}
                                    disabled={busy === "rename" || !nameDraft.trim()}
                                    className="text-stamp-red hover:text-stamp-red/70 disabled:opacity-40"
                                    data-testid="dossier-name-save"
                                    title="Save"
                                >
                                    {busy === "rename" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                                </button>
                                <button
                                    onClick={() => setEditingName(false)}
                                    className="text-ink-soft hover:text-ink"
                                    data-testid="dossier-name-cancel"
                                    title="Cancel"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-3">
                                {d.name}
                                {isInternal && !locked && (
                                    <button
                                        onClick={() => { setNameDraft(d.name || ""); setEditingName(true); }}
                                        className="text-ink-soft hover:text-stamp-red text-base"
                                        data-testid="dossier-name-edit"
                                        title="Rename dossier"
                                    >
                                        <Pencil className="w-5 h-5" />
                                    </button>
                                )}
                            </span>
                        )}
                    </h1>
                    <div className="font-accent text-xs text-ink-soft uppercase tracking-[0.2em] mt-2">
                        {d.entity} · {d.period_start?.slice(0, 10)} → {d.period_end?.slice(0, 10)} · Opening {fmt(d.opening_balance)} · Reserve {fmt(d.reserve_threshold)}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {isInternal && (
                        <button
                            onClick={aiSuggest}
                            disabled={busy === "ai" || locked}
                            className="inline-flex items-center gap-2 border border-ink/25 hover:border-stamp-red hover:text-stamp-red px-4 py-2 font-accent uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                            data-testid="ai-suggest-button"
                        >
                            {busy === "ai" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bot className="w-3 h-3" />}
                            AI Label Suggest
                        </button>
                    )}
                    <button
                        onClick={exportPdf}
                        disabled={busy === "pdf"}
                        className="inline-flex items-center gap-2 border border-ink/25 hover:border-stamp-red hover:text-stamp-red px-4 py-2 font-accent uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                        data-testid="export-pdf-button"
                    >
                        {busy === "pdf" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                        Export PDF
                    </button>
                    {isInternal && (
                        <button
                            onClick={toggleLock}
                            disabled={busy === "lock"}
                            className={`inline-flex items-center gap-2 px-4 py-2 font-accent uppercase text-xs tracking-[0.25em] border ${locked ? "border-stamp-green text-stamp-green hover:bg-stamp-green hover:text-black" : "border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-black"}`}
                            data-testid="toggle-lock-button"
                        >
                            {locked ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                            {locked ? "Unseal" : "Seal Dossier"}
                        </button>
                    )}
                </div>
            </div>

            {notice && (
                <div className="mb-4 border border-stamp-red text-stamp-red font-accent text-xs uppercase tracking-[0.25em] px-4 py-2" data-testid="dossier-notice">
                    // {notice}
                </div>
            )}

            <div className="grid grid-cols-12 gap-4">
                {/* Intake column */}
                <div className="col-span-12 lg:col-span-3 space-y-4">
                    <Panel title="// Intake" testId="panel-intake">
                        {isInternal && !locked ? (
                            <>
                                <div
                                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-stamp-red"); }}
                                    onDragLeave={(e) => e.currentTarget.classList.remove("border-stamp-red")}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.currentTarget.classList.remove("border-stamp-red");
                                        const f = e.dataTransfer.files?.[0];
                                        if (f) upload(f);
                                    }}
                                    className="border-2 border-dashed border-ink/25 p-8 text-center cursor-pointer hover:border-stamp-red transition-colors"
                                    onClick={() => fileRef.current?.click()}
                                    data-testid="upload-dropzone"
                                >
                                    {busy === "upload" ? (
                                        <div className="text-stamp-red font-accent text-xs uppercase tracking-[0.3em] flex items-center justify-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" /> Decrypting payload...
                                        </div>
                                    ) : (
                                        <>
                                            <Upload className="w-6 h-6 text-stamp-red mx-auto mb-3" strokeWidth={1.5} />
                                            <div className="font-accent text-xs uppercase tracking-[0.25em] text-ink">
                                                Transmit CSV / PDF
                                            </div>
                                            <div className="font-accent text-[10px] uppercase tracking-[0.25em] text-ink-faint mt-2">
                                                Drop or click · bank statements, invoices
                                            </div>
                                        </>
                                    )}
                                </div>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".csv,.pdf,text/csv,application/pdf"
                                    className="hidden"
                                    onChange={(e) => e.target.files[0] && upload(e.target.files[0])}
                                    data-testid="upload-file-input"
                                />
                                <ManualEntry dossierId={id} onAdded={loadAll} />
                            </>
                        ) : (
                            <div className="font-accent text-[10px] uppercase tracking-[0.25em] text-ink-faint border border-dashed border-ink/25 p-4">
                                {locked ? "// Dossier sealed — ingestion locked" : "// Read-only access"}
                            </div>
                        )}
                    </Panel>

                    <Panel title="// Flags Summary" testId="panel-flags">
                        <FlagSummary txns={txns} />
                    </Panel>
                </div>

                {/* Middle column: tabs */}
                <div className="col-span-12 lg:col-span-6">
                    <div className="flex border-b border-ink/25 font-accent text-[10px] uppercase tracking-[0.3em]">
                        {[
                            { k: "adjust", label: "Adjust Ledger", icon: FileDigit },
                            { k: "reports", label: "Reports", icon: Activity },
                            { k: "predict", label: "Predict", icon: TrendingDown },
                            { k: "audit", label: "Audit Trail", icon: History },
                        ].map((t) => (
                            <button
                                key={t.k}
                                onClick={() => setTab(t.k)}
                                className={`px-4 py-3 border-b-2 -mb-px flex items-center gap-2 ${tab === t.k ? "border-stamp-red text-stamp-red" : "border-transparent text-ink-soft hover:text-white"}`}
                                data-testid={`tab-${t.k}`}
                            >
                                <t.icon className="w-3 h-3" /> {t.label}
                            </button>
                        ))}
                    </div>

                    <div className="border border-t-0 border-ink/25 bg-paper-light p-0">
                        {tab === "adjust" && (
                            <TxnTable
                                txns={txns}
                                locked={locked}
                                onChanged={loadAll}
                            />
                        )}
                        {tab === "reports" && reports && <Reports reports={reports} />}
                        {tab === "predict" && predict && <Predict predict={predict} />}
                        {tab === "audit" && <AuditView audit={audit} />}
                    </div>
                </div>

                {/* Right column: Prediction summary */}
                <div className="col-span-12 lg:col-span-3 space-y-4">
                    {predict && <PredictSummary predict={predict} />}
                    {reports && <PnlCard pnl={reports.pnl} />}
                </div>
            </div>
        </Shell>
    );
}

function Panel({ title, testId, children }) {
    return (
        <div className="border border-ink/25 bg-paper-light" data-testid={testId}>
            <div className="px-4 py-2 border-b border-ink/25 font-accent text-[10px] uppercase tracking-[0.3em] text-ink-soft">
                {title}
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

function ManualEntry({ dossierId, onAdded }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        date: new Date().toISOString().slice(0, 10),
        description: "",
        amount: 0,
        category: "",
        vendor: "",
    });
    const add = async (e) => {
        e.preventDefault();
        await api.post(`/dossiers/${dossierId}/transactions`, form);
        setForm({ ...form, description: "", amount: 0, category: "", vendor: "" });
        setOpen(false);
        onAdded();
    };
    if (!open)
        return (
            <button
                onClick={() => setOpen(true)}
                className="mt-4 w-full border border-ink/25 hover:border-stamp-red hover:text-stamp-red px-3 py-2 font-accent uppercase text-[10px] tracking-[0.3em] inline-flex items-center justify-center gap-2"
                data-testid="manual-entry-open"
            >
                <Plus className="w-3 h-3" /> Manual transmission
            </button>
        );
    return (
        <form onSubmit={add} className="mt-4 space-y-2 border border-ink/25 p-3" data-testid="manual-entry-form">
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className="w-full px-2 py-1 text-xs" data-testid="manual-date" />
            <input placeholder="description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required className="w-full px-2 py-1 text-xs" data-testid="manual-desc" />
            <input type="number" step="0.01" placeholder="amount (neg=expense)" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} required className="w-full px-2 py-1 text-xs" data-testid="manual-amount" />
            <input placeholder="vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="w-full px-2 py-1 text-xs" data-testid="manual-vendor" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-2 py-1 text-xs" data-testid="manual-category">
                <option value="">— category —</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex gap-2">
                <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-ink/25 px-2 py-1 text-[10px] font-accent uppercase tracking-[0.25em]" data-testid="manual-cancel">Cancel</button>
                <button type="submit" className="flex-1 bg-stamp-red text-black px-2 py-1 text-[10px] font-accent uppercase tracking-[0.25em] hover:bg-stamp-red" data-testid="manual-submit">Commit</button>
            </div>
        </form>
    );
}

function FlagSummary({ txns }) {
    const counts = {};
    let total = 0;
    for (const t of txns) {
        total++;
        for (const f of (t.gap_flags || [])) counts[f] = (counts[f] || 0) + 1;
    }
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return (
        <div className="font-accent text-xs">
            <div className="text-ink-soft uppercase tracking-[0.2em] mb-2">
                {total} transmissions · {entries.length} gap types
            </div>
            {entries.length === 0 ? (
                <div className="text-stamp-green uppercase tracking-[0.25em] text-[10px]">
                    ● All clean
                </div>
            ) : (
                <ul className="space-y-1">
                    {entries.map(([k, v]) => (
                        <li key={k} className="flex justify-between border-b border-ink/15 pb-1">
                            <span className="text-stamp-red text-[10px] uppercase tracking-[0.2em]">
                                <AlertTriangle className="w-3 h-3 inline mr-1" />{k}
                            </span>
                            <span className="text-stamp-red">{v}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function TxnTable({ txns, locked, onChanged }) {
    const [editing, setEditing] = useState(null);
    if (txns.length === 0) {
        return (
            <div className="p-8 text-center font-accent text-xs text-ink-faint uppercase tracking-[0.3em]" data-testid="txn-empty">
                // No transmissions in ledger
            </div>
        );
    }
    return (
        <div className="overflow-x-auto" data-testid="txn-table">
            <table className="w-full font-accent text-[11px]">
                <thead className="text-ink-soft uppercase tracking-[0.2em]">
                    <tr className="border-b border-ink/25">
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-left px-3 py-2">Description</th>
                        <th className="text-left px-3 py-2">Vendor</th>
                        <th className="text-left px-3 py-2">Category</th>
                        <th className="text-right px-3 py-2">Amount</th>
                        <th className="text-left px-3 py-2">Flags</th>
                        <th className="px-3 py-2"></th>
                    </tr>
                </thead>
                <tbody>
                    {txns.map((t) => (
                        <TxnRow key={t.id} t={t} locked={locked} onChanged={onChanged} isEditing={editing === t.id} setEditing={setEditing} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function TxnRow({ t, locked, onChanged, isEditing, setEditing }) {
    const [form, setForm] = useState({ category: t.category || "", vendor: t.vendor || "", description: t.description });
    const [splits, setSplits] = useState(t.splits && t.splits.length ? t.splits : []);
    const [showSplit, setShowSplit] = useState(!!(t.splits && t.splits.length));

    const save = async () => {
        const body = { ...form };
        if (showSplit && splits.length > 0) {
            body.splits = splits;
        } else if (!showSplit && t.is_fractional) {
            body.splits = [];
            body.is_fractional = false;
        }
        try {
            await api.patch(`/transactions/${t.id}`, body);
            setEditing(null);
            onChanged();
        } catch (e) {
            alert(e.response?.data?.detail || "Save failed");
        }
    };

    const remove = async () => {
        if (!window.confirm("Redact this transmission?")) return;
        await api.delete(`/transactions/${t.id}`);
        onChanged();
    };

    const flagColor = (f) => "text-stamp-red";
    const amtColor = t.amount < 0 ? "text-stamp-red" : "text-stamp-green";

    if (!isEditing) {
        return (
            <tr className="border-b border-ink/15 hover:bg-paper-light" data-testid={`txn-row-${t.id}`}>
                <td className="px-3 py-2 text-ink-soft">{t.date?.slice(0, 10)}</td>
                <td className="px-3 py-2 text-ink max-w-[240px] truncate">{t.description}</td>
                <td className="px-3 py-2 text-ink-soft">{t.vendor || "—"}</td>
                <td className="px-3 py-2">
                    {t.category ? (
                        <span className="text-stamp-red">{t.category}</span>
                    ) : (
                        <span className="text-stamp-red">—</span>
                    )}
                    {t.source === "ai" && (
                        <span className="ml-2 border border-stamp-green text-stamp-green text-[9px] px-1">AI {Math.round((t.confidence || 0) * 100)}%</span>
                    )}
                    {t.is_fractional && (
                        <span className="ml-2 border border-stamp-red text-stamp-red text-[9px] px-1">SPLIT×{t.splits.length}</span>
                    )}
                </td>
                <td className={`px-3 py-2 text-right ${amtColor}`}>{fmt(t.amount)}</td>
                <td className="px-3 py-2">
                    {(t.gap_flags || []).map((f) => (
                        <span key={f} className={`text-[9px] ${flagColor(f)} uppercase tracking-wide mr-1`}>!{f.replace("missing_", "")}</span>
                    ))}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                    {!locked && (
                        <>
                            <button onClick={() => setEditing(t.id)} className="text-ink-soft hover:text-stamp-red px-1" data-testid={`txn-edit-${t.id}`}>edit</button>
                            <button onClick={remove} className="text-ink-soft hover:text-stamp-red px-1" data-testid={`txn-delete-${t.id}`}><Trash2 className="w-3 h-3 inline" /></button>
                        </>
                    )}
                </td>
            </tr>
        );
    }

    return (
        <tr className="border-b border-stamp-red bg-paper-light" data-testid={`txn-edit-row-${t.id}`}>
            <td className="px-3 py-2 text-ink-soft" colSpan={7}>
                <div className="grid grid-cols-6 gap-2 items-end">
                    <div className="col-span-2">
                        <label className="text-[9px] text-ink-faint uppercase tracking-[0.25em]">description</label>
                        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-2 py-1 text-xs" data-testid={`edit-desc-${t.id}`} />
                    </div>
                    <div>
                        <label className="text-[9px] text-ink-faint uppercase tracking-[0.25em]">vendor</label>
                        <input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="w-full px-2 py-1 text-xs" data-testid={`edit-vendor-${t.id}`} />
                    </div>
                    <div>
                        <label className="text-[9px] text-ink-faint uppercase tracking-[0.25em]">category</label>
                        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-2 py-1 text-xs" data-testid={`edit-category-${t.id}`}>
                            <option value="">—</option>
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <button onClick={() => setShowSplit(!showSplit)} className="w-full border border-ink/25 hover:border-stamp-red px-2 py-1 text-[10px] font-accent uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1" data-testid={`edit-split-toggle-${t.id}`}>
                            <Split className="w-3 h-3" /> {showSplit ? "Unsplit" : "Fractional"}
                        </button>
                    </div>
                    <div className="flex gap-1">
                        <button onClick={save} className="flex-1 bg-stamp-red text-black px-2 py-1 text-[10px] font-accent uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1" data-testid={`edit-save-${t.id}`}><Save className="w-3 h-3" /> Save</button>
                        <button onClick={() => setEditing(null)} className="flex-1 border border-ink/25 px-2 py-1 text-[10px] font-accent uppercase tracking-[0.2em]" data-testid={`edit-cancel-${t.id}`}>Cancel</button>
                    </div>
                </div>
                {showSplit && (
                    <SplitEditor total={t.amount} splits={splits} setSplits={setSplits} txnId={t.id} />
                )}
            </td>
        </tr>
    );
}

function SplitEditor({ total, splits, setSplits, txnId }) {
    const sum = splits.reduce((s, x) => s + Number(x.amount || 0), 0);
    const diff = Number(total) - sum;
    const addLeg = () => setSplits([...splits, { category: "", amount: 0, vendor: "" }]);
    const upd = (i, k, v) => setSplits(splits.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
    const rm = (i) => setSplits(splits.filter((_, idx) => idx !== i));
    return (
        <div className="mt-3 border border-ink/25 p-3" data-testid={`split-editor-${txnId}`}>
            <div className="flex justify-between font-accent text-[10px] uppercase tracking-[0.25em] text-ink-soft mb-2">
                <span>// Fractional legs · target {fmt(total)}</span>
                <span className={Math.abs(diff) < 0.01 ? "text-stamp-green" : "text-stamp-red"}>Δ {fmt(diff)}</span>
            </div>
            {splits.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                    <select value={s.category} onChange={(e) => upd(i, "category", e.target.value)} className="col-span-4 px-2 py-1 text-xs" data-testid={`split-cat-${txnId}-${i}`}>
                        <option value="">—</option>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input placeholder="vendor" value={s.vendor || ""} onChange={(e) => upd(i, "vendor", e.target.value)} className="col-span-4 px-2 py-1 text-xs" data-testid={`split-vendor-${txnId}-${i}`} />
                    <input type="number" step="0.01" value={s.amount} onChange={(e) => upd(i, "amount", parseFloat(e.target.value) || 0)} className="col-span-3 px-2 py-1 text-xs text-right" data-testid={`split-amount-${txnId}-${i}`} />
                    <button onClick={() => rm(i)} className="col-span-1 text-stamp-red hover:text-white" data-testid={`split-remove-${txnId}-${i}`}><Trash2 className="w-3 h-3" /></button>
                </div>
            ))}
            <button onClick={addLeg} className="w-full border border-dashed border-ink/25 hover:border-stamp-red px-2 py-1 text-[10px] font-accent uppercase tracking-[0.25em] inline-flex items-center justify-center gap-1" data-testid={`split-add-${txnId}`}>
                <Plus className="w-3 h-3" /> Add leg
            </button>
        </div>
    );
}

function Reports({ reports }) {
    const cash = reports.cashflow.map((r) => ({ ...r }));
    return (
        <div className="p-4 space-y-6" data-testid="reports-panel">
            <div>
                <h3 className="font-accent text-[10px] uppercase tracking-[0.3em] text-stamp-red mb-2">// Cashflow — monthly</h3>
                <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={cash}>
                            <CartesianGrid stroke="#4A443C" strokeDasharray="2 4" />
                            <XAxis dataKey="month" stroke="#4A443C" />
                            <YAxis stroke="#4A443C" />
                            <Tooltip contentStyle={{ background: "#F4ECD8", border: "1px solid #4A443C", color: "#1A1714", fontFamily: "Courier Prime" }} />
                            <Line type="step" dataKey="income" stroke="#3B6E3B" strokeWidth={1.5} dot={false} />
                            <Line type="step" dataKey="expense" stroke="#B22020" strokeWidth={1.5} dot={false} />
                            <Line type="step" dataKey="net" stroke="#1A1714" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <CategoryBars title="// Expense by category" data={reports.by_category_expense.slice(0, 10)} color="#B22020" testId="expense-chart" />
                <CategoryBars title="// Income by category" data={reports.by_category_income.slice(0, 10)} color="#3B6E3B" testId="income-chart" />
            </div>
            <div>
                <h3 className="font-accent text-[10px] uppercase tracking-[0.3em] text-stamp-red mb-2">// Top vendors (burn)</h3>
                <table className="w-full font-accent text-xs" data-testid="top-vendors-table">
                    <thead><tr className="border-b border-ink/25 text-ink-faint uppercase tracking-[0.2em]"><th className="text-left px-3 py-1">Vendor</th><th className="text-right px-3 py-1">Amount</th></tr></thead>
                    <tbody>
                        {reports.top_vendors.map((v) => (
                            <tr key={v.vendor} className="border-b border-ink/15">
                                <td className="px-3 py-1 text-ink">{v.vendor}</td>
                                <td className="px-3 py-1 text-right text-stamp-red">{fmt(v.amount)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CategoryBars({ title, data, color, testId }) {
    return (
        <div data-testid={testId}>
            <h3 className="font-accent text-[10px] uppercase tracking-[0.3em] text-stamp-red mb-2">{title}</h3>
            <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} layout="vertical" margin={{ left: 80 }}>
                        <CartesianGrid stroke="#4A443C" strokeDasharray="2 4" horizontal={false} />
                        <XAxis type="number" stroke="#4A443C" />
                        <YAxis type="category" dataKey="category" stroke="#1A1714" width={80} />
                        <Tooltip contentStyle={{ background: "#F4ECD8", border: "1px solid #4A443C", color: "#1A1714", fontFamily: "Courier Prime" }} />
                        <Bar dataKey="amount" fill={color} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function Predict({ predict }) {
    const pareto = predict.cost_pareto.slice(0, 12);
    return (
        <div className="p-4 space-y-6" data-testid="predict-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Runway" value={predict.runway_months != null ? `${predict.runway_months.toFixed(1)} mo` : "∞"} testId="stat-runway" />
                <Stat label="Closing" value={predict.closing_date || "N/A"} color="text-stamp-red" testId="stat-closing" />
                <Stat label="Net burn/mo" value={fmt(predict.net_monthly_burn)} color={predict.net_monthly_burn > 0 ? "text-stamp-red" : "text-stamp-green"} testId="stat-burn" />
                <Stat label="Confidence" value={`${Math.round(predict.confidence * 100)}%`} testId="stat-confidence" />
            </div>
            <div>
                <h3 className="font-accent text-[10px] uppercase tracking-[0.3em] text-stamp-red mb-2">// Cost concentration (Pareto 80% zone highlighted)</h3>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={pareto} layout="vertical" margin={{ left: 90 }}>
                            <CartesianGrid stroke="#4A443C" strokeDasharray="2 4" horizontal={false} />
                            <XAxis type="number" stroke="#4A443C" />
                            <YAxis type="category" dataKey="category" stroke="#1A1714" width={90} />
                            <Tooltip contentStyle={{ background: "#F4ECD8", border: "1px solid #4A443C", color: "#1A1714", fontFamily: "Courier Prime" }} />
                            <Bar dataKey="amount">
                                {pareto.map((p, i) => (
                                    <Cell key={i} fill={p.cumulative_share <= 0.8 ? "#B22020" : "#7A6F5F"} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <div>
                <h3 className="font-accent text-[10px] uppercase tracking-[0.3em] text-stamp-red mb-2">// Income uplift required to extend runway</h3>
                <table className="w-full font-accent text-xs" data-testid="income-gap-table">
                    <thead><tr className="border-b border-ink/25 text-ink-faint uppercase tracking-[0.2em]"><th className="text-left px-3 py-2">Extend by</th><th className="text-right px-3 py-2">Monthly uplift</th><th className="text-right px-3 py-2">Total period</th></tr></thead>
                    <tbody>
                        {predict.income_gap_scenarios.map((s) => (
                            <tr key={s.extend_months} className="border-b border-ink/15">
                                <td className="px-3 py-2 text-ink">+{s.extend_months} months</td>
                                <td className="px-3 py-2 text-right text-stamp-red">{fmt(s.required_monthly_income_uplift)}</td>
                                <td className="px-3 py-2 text-right text-ink-soft">{fmt(s.required_total_uplift_over_period)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {predict.income_category_targets?.length > 0 && (
                <div>
                    <h3 className="font-accent text-[10px] uppercase tracking-[0.3em] text-stamp-red mb-2">
                        // Income category growth targets (proportional to current share, +3 mo runway)
                    </h3>
                    <table className="w-full font-accent text-xs" data-testid="income-category-targets">
                        <thead><tr className="border-b border-ink/25 text-ink-faint uppercase tracking-[0.2em]"><th className="text-left px-3 py-2">Category</th><th className="text-right px-3 py-2">Current / mo</th><th className="text-right px-3 py-2">Additional target / mo</th></tr></thead>
                        <tbody>
                            {predict.income_category_targets.map((s) => (
                                <tr key={s.category} className="border-b border-ink/15">
                                    <td className="px-3 py-2 text-ink">{s.category}</td>
                                    <td className="px-3 py-2 text-right text-stamp-green">{fmt(s.current_monthly)}</td>
                                    <td className="px-3 py-2 text-right text-stamp-red">{fmt(s.target_monthly_uplift)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, color = "text-stamp-red", testId }) {
    return (
        <div className="border border-ink/25 p-3" data-testid={testId}>
            <div className="font-accent text-[10px] uppercase tracking-[0.3em] text-ink-soft">{label}</div>
            <div className={`font-heading text-3xl mt-1 ${color}`}>{value}</div>
        </div>
    );
}

function PredictSummary({ predict }) {
    return (
        <div className="border border-ink/25 bg-paper-light p-4" data-testid="predict-summary">
            <div className="font-accent text-[10px] uppercase tracking-[0.3em] text-ink-soft mb-2">// Predictive Assessment</div>
            <div className="font-accent text-[10px] uppercase tracking-[0.3em] text-ink-faint">Projected closing</div>
            <div className="font-heading text-4xl text-stamp-red tracking-widest" data-testid="summary-closing-date">
                {predict.closing_date || "∞"}
            </div>
            <div className="hairline my-3" />
            <dl className="space-y-2 font-accent text-xs">
                <Row k="Balance" v={fmt(predict.current_balance)} />
                <Row k="Reserve" v={fmt(predict.reserve_threshold)} />
                <Row k="Avg income/mo" v={fmt(predict.avg_monthly_income)} c="text-stamp-green" />
                <Row k="Avg expense/mo" v={fmt(predict.avg_monthly_expense)} c="text-stamp-red" />
                <Row k="Net burn/mo" v={fmt(predict.net_monthly_burn)} c={predict.net_monthly_burn > 0 ? "text-stamp-red" : "text-stamp-green"} />
                <Row k="Runway" v={predict.runway_months != null ? `${predict.runway_months.toFixed(1)} mo` : "∞"} c="text-stamp-red" />
                <Row k="Confidence" v={`${Math.round(predict.confidence * 100)}%`} />
            </dl>
        </div>
    );
}

function Row({ k, v, c = "text-ink" }) {
    return (
        <div className="flex justify-between border-b border-ink/15 pb-1">
            <dt className="text-ink-soft uppercase tracking-[0.2em]">{k}</dt>
            <dd className={c}>{v}</dd>
        </div>
    );
}

function PnlCard({ pnl }) {
    return (
        <div className="border border-ink/25 bg-paper-light p-4" data-testid="pnl-card">
            <div className="font-accent text-[10px] uppercase tracking-[0.3em] text-ink-soft mb-2">// P&amp;L</div>
            <dl className="space-y-2 font-accent text-xs">
                <Row k="Income" v={fmt(pnl.total_income)} c="text-stamp-green" />
                <Row k="Expense" v={fmt(pnl.total_expense)} c="text-stamp-red" />
                <Row k="Net" v={fmt(pnl.net)} c={pnl.net >= 0 ? "text-stamp-green" : "text-stamp-red"} />
            </dl>
        </div>
    );
}

function AuditView({ audit }) {
    if (audit.length === 0)
        return <div className="p-8 text-center text-ink-faint font-accent text-xs uppercase tracking-[0.3em]" data-testid="audit-empty">// No mutations logged</div>;
    return (
        <div className="overflow-x-auto" data-testid="audit-panel">
            <table className="w-full font-accent text-[11px]">
                <thead className="text-ink-soft uppercase tracking-[0.2em]">
                    <tr className="border-b border-ink/25">
                        <th className="text-left px-3 py-2">Timestamp</th>
                        <th className="text-left px-3 py-2">Actor</th>
                        <th className="text-left px-3 py-2">Action</th>
                        <th className="text-left px-3 py-2">Note</th>
                        <th className="text-left px-3 py-2">Hash</th>
                    </tr>
                </thead>
                <tbody>
                    {audit.map((a) => (
                        <tr key={a.id} className="border-b border-ink/15 hover:bg-paper-light" data-testid={`audit-row-${a.id}`}>
                            <td className="px-3 py-2 text-ink-soft">{a.timestamp.replace("T", " ").slice(0, 19)}</td>
                            <td className="px-3 py-2 text-ink">{a.actor_name}</td>
                            <td className="px-3 py-2 text-stamp-red">{a.action}</td>
                            <td className="px-3 py-2 text-ink-soft">{a.note || "—"}</td>
                            <td className="px-3 py-2 text-ink-faint font-mono">{a.hash?.slice(0, 10)}…</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
